import { apiInstance, buyerCaptureException } from '@diamond/shared/utils';
import { AxiosError } from 'axios';

import { captureAdminDataAccessError } from './capture-admin-data-access-error';

interface QueryStringSearch {
  [key: string]: string | number | boolean | null | string[];
}

/**
 * Creates a wrapper for making DELETE requests to an API endpoint with error handling and exception capture.
 * Provides multiple delete functions for different deletion patterns.
 *
 * @template T - The expected response type from the API
 * @param url - The base URL endpoint for DELETE requests (without trailing slash)
 * @param token - Optional authentication token to be used with API requests
 * @param captureException - Optional exception capture mode:
 *   - 'admin': Captures exceptions using the shared admin API capture helper
 *   - 'buyer': Captures exceptions using buyerCaptureException
 *   - undefined: No exception capturing
 *
 * @returns Object containing multiple delete functions:
 *   - deleteData: Basic delete by ID
 *   - deleteDataCustomUrl: Delete with custom URL path
 *   - deleteDataWithObject: Delete with object payload for product deletion
 *   - deleteDataWithCustomObject: Delete with custom object payload
 *
 * @example
 * ```typescript
 * const {
 *   deleteData,
 *   deleteDataCustomUrl,
 *   deleteDataWithObject
 * } = DeleteDataWrapper<ApiResponse>(
 *   '/api/users',
 *   'your-auth-token',
 *   'admin'
 * );
 *
 * // Basic deletion by ID
 * await deleteData(123);
 *
 * // Custom URL deletion
 * await deleteDataCustomUrl('archive/456');
 * ```
 */
export function DeleteDataWrapper<T>(
  url: string,
  token?: string,
  captureException?: 'admin' | 'buyer'
) {
  /**
   * Basic delete function that removes a resource by its ID.
   *
   * @template T - The type of the response data
   * @param id - The ID of the resource to delete
   *
   * @returns Promise resolving to the API response data
   *
   * @throws Error with the API error message if deletion fails
   *
   * @example
   * ```typescript
   * // Delete user with ID 123
   * const result = await deleteData<DeleteResponse>(123);
   * // Request URL: /api/users/123
   * ```
   */
  async function deleteData<T>(id: number | string): Promise<unknown> {
    const requestPath = `${url}/${id}`;
    try {
      const response = await apiInstance(token).delete<T>(requestPath);
      if (response.status !== 200) {
        throw new Error('something goes wrong');
      }
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        if (captureException === 'admin') {
          const responseData = error.response?.data;
          throw captureAdminDataAccessError({
            error,
            message: responseData?.message,
            requestPath,
            httpMethod: 'DELETE',
            queryKey: 'DeleteDataWrapper error',
            operation: 'delete_data',
            extras: { id },
            level: 'error',
          });
        }
        if (captureException === 'buyer') {
          buyerCaptureException(error.response?.data, (scope) => {
            scope.setTags({
              queryKey: 'DeleteDataWrapper error',
              status_code:
                error instanceof AxiosError
                  ? error.response?.data.statusCode
                  : undefined,
            });
            scope.setContext('error', {
              error:
                error instanceof AxiosError ? error.response?.data : undefined,
            });
            scope.setExtra(url, id);
            scope.setLevel('error');
          });
          throw new Error(error.response?.data.message);
        }
      }
      return;
    }
  }

  /**
   * Delete function with custom URL path support.
   * Allows for more complex deletion patterns with custom URL segments.
   *
   * @template T - The type of the response data
   * @param custUrl - Custom URL segment to append to the base URL
   *
   * @returns Promise resolving to the API response data
   *
   * @throws Error with the API error message if deletion fails
   *
   * @example
   * ```typescript
   * // Delete with custom URL path
   * const result = await deleteDataCustomUrl<DeleteResponse>('archive/456');
   * // Request URL: /api/users/archive/456
   *
   * // Delete with nested resource
   * const result = await deleteDataCustomUrl('123/posts/789');
   * // Request URL: /api/users/123/posts/789
   * ```
   */
  async function deleteDataCustomUrl<T>(
    custUrl: number | string
  ): Promise<unknown> {
    try {
      const response = await apiInstance(token).delete<T>(`${url}/${custUrl}`);
      if (response.status !== 200) {
        throw new Error('something goes wrong');
      }
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data.message);
      }
      return;
    }
  }

  /**
   * Delete function specifically designed for product deletion with object payload.
   * Sends product_id in the request body for bulk or specific product deletion.
   *
   * @template T - The type of the response data
   * @param custUrl - Custom URL segment to append to the base URL
   * @param id - Product ID or array of product IDs to delete
   *
   * @returns Promise resolving to the API response data
   *
   * @throws Error with the API error message if deletion fails
   *
   * @example
   * ```typescript
   * // Delete single product
   * const result = await deleteDataWithObject<DeleteResponse>('products', '123');
   * // Request URL: /api/users/products
   * // Request body: { product_id: '123' }
   *
   * // Delete multiple products
   * const result = await deleteDataWithObject('bulk-delete', ['123', '456', '789']);
   * // Request body: { product_id: ['123', '456', '789'] }
   * ```
   */
  async function deleteDataWithObject<T>(
    custUrl: number | string,
    id: string | string[]
  ): Promise<unknown> {
    try {
      const response = await apiInstance(token).delete<T>(`${url}/${custUrl}`, {
        data: {
          product_id: id,
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data.message);
      }
      return;
    }
  }

  /**
   * Delete function with fully customizable object payload.
   * Provides maximum flexibility for complex deletion scenarios.
   *
   * @template T - The type of the response data
   * @param custUrl - Custom URL segment to append to the base URL
   * @param payload - Custom object payload to send in the request body
   *
   * @returns Promise resolving to the API response data
   *
   * @throws Error with the API error message if deletion fails
   *
   * @example
   * ```typescript
   * // Delete with custom criteria
   * const result = await deleteDataWithCustomObject<DeleteResponse>(
   *   'conditional-delete',
   *   {
   *     user_ids: ['123', '456'],
   *     reason: 'bulk_cleanup',
   *     force: true,
   *     created_before: '2023-01-01'
   *   }
   * );
   * // Request URL: /api/users/conditional-delete
   * // Request body: { user_ids: [...], reason: '...', force: true, ... }
   * ```
   */
  async function deleteDataWithCustomObject<T>(
    custUrl: number | string,
    payload?: Readonly<QueryStringSearch>
  ): Promise<unknown> {
    try {
      const response = await apiInstance(token).delete<T>(`${url}/${custUrl}`, {
        data: payload,
      });
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data.message);
      }
      return;
    }
  }

  return {
    deleteData,
    deleteDataCustomUrl,
    deleteDataWithObject,
    deleteDataWithCustomObject,
  };
}
